import { useRef, useState, useCallback, useEffect } from "react";
import { ChevronsLeftRight } from "lucide-react";

export default function CompareSlider({ before, after, title, testid }) {
  const ref = useRef(null);
  const [pos, setPos] = useState(50);

  const move = useCallback((clientX) => {
    const r = ref.current?.getBoundingClientRect();
    if (!r || clientX == null) return;
    setPos(Math.min(97, Math.max(3, ((clientX - r.left) / r.width) * 100)));
  }, []);

  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const clientXOf = (ev) => (ev.touches && ev.touches[0] ? ev.touches[0].clientX : ev.clientX);
    const onDown = (ev) => {
      move(clientXOf(ev));
      const onMove = (e2) => move(clientXOf(e2));
      const onUp = () => {
        window.removeEventListener("mousemove", onMove);
        window.removeEventListener("mouseup", onUp);
        window.removeEventListener("touchmove", onMove);
        window.removeEventListener("touchend", onUp);
        window.removeEventListener("pointermove", onMove);
        window.removeEventListener("pointerup", onUp);
      };
      window.addEventListener("mousemove", onMove);
      window.addEventListener("mouseup", onUp);
      window.addEventListener("touchmove", onMove, { passive: true });
      window.addEventListener("touchend", onUp);
      window.addEventListener("pointermove", onMove);
      window.addEventListener("pointerup", onUp);
    };
    el.addEventListener("mousedown", onDown);
    el.addEventListener("touchstart", onDown, { passive: true });
    el.addEventListener("pointerdown", onDown);
    return () => {
      el.removeEventListener("mousedown", onDown);
      el.removeEventListener("touchstart", onDown);
      el.removeEventListener("pointerdown", onDown);
    };
  }, [move]);

  const onKey = (e) => {
    if (e.key === "ArrowLeft") setPos((p) => Math.max(3, p - 4));
    if (e.key === "ArrowRight") setPos((p) => Math.min(97, p + 4));
  };

  return (
    <div
      ref={ref}
      data-testid={testid}
      role="slider"
      aria-label={`${title} before and after comparison`}
      aria-valuenow={Math.round(pos)}
      aria-valuemin={0}
      aria-valuemax={100}
      tabIndex={0}
      onKeyDown={onKey}
      className="relative aspect-[4/3] overflow-hidden rounded-sm border border-gold/25 select-none touch-none cursor-ew-resize outline-none focus:border-gold/60 bg-[#0D0D10]"
    >
      <img src={after} alt={`${title} — after clean-up`} draggable="false" className="absolute inset-0 w-full h-full object-cover pointer-events-none" />
      <img
        src={before}
        alt={`${title} — before clean-up`}
        draggable="false"
        className="absolute inset-0 w-full h-full object-cover pointer-events-none"
        style={{ clipPath: `inset(0 ${100 - pos}% 0 0)` }}
      />
      <span className="absolute top-4 left-4 font-mono-label text-[10px] uppercase tracking-[0.2em] bg-[#0A0A0C]/85 border border-gold/50 text-gold px-3 py-1.5 rounded-sm backdrop-blur-sm pointer-events-none">
        Before
      </span>
      <span className="absolute top-4 right-4 font-mono-label text-[10px] uppercase tracking-[0.2em] bg-[#0A0A0C]/85 border border-gold/50 text-gold px-3 py-1.5 rounded-sm backdrop-blur-sm pointer-events-none">
        After
      </span>
      <div className="absolute inset-y-0 pointer-events-none" style={{ left: `${pos}%` }}>
        <div className="absolute inset-y-0 -translate-x-1/2 w-[2px] bg-gradient-to-b from-[#FCF6BA] via-[#D4AF37] to-[#AA771C]" />
        <div className="absolute top-1/2 -translate-x-1/2 -translate-y-1/2 w-11 h-11 rounded-full bg-[#0A0A0C]/90 border-2 border-gold flex items-center justify-center shadow-[0_0_20px_rgba(212,175,55,0.4)]">
          <ChevronsLeftRight size={18} className="text-gold" />
        </div>
      </div>
    </div>
  );
}
